/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import * as H from 'history';
import { ViewModel } from '../../../../core/viewmodel/ViewModel';
import { StoreProvider } from '../../../../stores/StoreProvider';
import { makeObservable, observable } from 'mobx';
import { useLocation } from 'react-router-dom';
import { Entry, SelectedOptionsMap } from '../../../../types/course/Entry';
import { EntryService } from '../../../../services/EntryService';
import { AuthStore } from '../../../../stores/application/AuthStore';
import { SnackbarStore } from '../../../../stores/application/SnackbarStore';
import { EntriesReviewStore } from '../../../../stores/components/EntriesReviewStore';
import { CourseService } from '../../../../services/CourseService';
import { ModuleService } from '../../../../services/ModuleService';
import { ModulePackage } from '../../../../types/course/ModulePackage';

/**
 * ViewModel for the Map view component
 * Handles the state and business logic for the map display
 *
 * @author Julien Tietke <j.tietke@tj-labs.de>
 */
export class MapViewModel extends ViewModel {
	private pathName = useLocation().pathname;

	// Initial map viewport settings (centered on the geographic midpoint of Germany)
	public viewState = {
		longitude: 10.4541194,
		latitude: 51.1787426,
		zoom: 4,
	};

	public approvedEntries: Entry[] = [];
	public isLoadingEntries = true;

	private readonly authStore: AuthStore;
	private readonly snackbarStore: SnackbarStore;
	private readonly entryService: EntryService;
	private readonly entriesReviewStore: EntriesReviewStore;
	private readonly courseService: CourseService;
	private readonly moduleService: ModuleService;
	private readonly courseId: string;
	private modulePackages: ModulePackage[] = [];

	constructor(storeProvider: StoreProvider, history: H.History) {
		super(storeProvider, history);

		this.authStore = storeProvider.authStore;
		this.snackbarStore = storeProvider.snackbarStore;
		this.entryService = storeProvider.entryService;
		this.entriesReviewStore = storeProvider.entriesReviewStore;
		this.courseService = storeProvider.courseService;
		this.moduleService = storeProvider.moduleService;

		// Extract courseId from pathname (format: /courses/{courseId}/map)
		this.courseId = this.pathName.substring(this.pathName.indexOf('/', 1) + 1, this.pathName.lastIndexOf('/'));

		makeObservable(this, {
			viewState: observable,
			approvedEntries: observable,
			isLoadingEntries: observable,
		});

		this.loadApprovedEntries();
		this.loadCourseModules();
	}
	public getModulePackages(): ModulePackage[] {
		return this.modulePackages;
	}

	/**
	 * Groups approved entries by their coordinates (rounded to 5 decimals)
	 * to support stacking markers when multiple entries share the same location
	 *
	 * @returns An array of entry groups
	 * @author Julien Tietke <j.tietke@tj-labs.de>
	 */
	public getGroupedEntries(): Entry[][] {
		return this.groupEntries(this.approvedEntries);
	}

	private async loadCourseModules(): Promise<void> {
		if (!this.courseId) {
			return;
		}

		const authToken = this.authStore.getAuthToken();

		try {
			const baseModulePackages = await this.courseService.getCourseModulePackages(authToken, this.courseId);

			this.modulePackages = await Promise.all(
				baseModulePackages.map(async (modulePackage) => {
					try {
						const detailedPackage = await this.moduleService.getModulePackage(authToken, modulePackage.id);
						const modulesWithElements = await Promise.all(
							(detailedPackage.modules ?? []).map(async (module) => {
								if (module?.elements && module.elements.length > 0) {
									return module;
								}

								try {
									return await this.moduleService.getModule(authToken, module.id);
								} catch {
									return module;
								}
							}),
						);

						return {
							...modulePackage,
							modules: modulesWithElements.map((module) => ({
								id: module.id,
								name: module.name,
								description: module.description,
								unitNumber: module.unitNumber,
								elements: module.elements,
							})),
						};
					} catch (error) {
						console.error('MapViewModel - Error loading module package details:', error);
						return {
							...modulePackage,
							modules: [],
						};
					}
				}),
			);
		} catch (error) {
			console.error('MapViewModel - Error loading module packages:', error);
			this.snackbarStore.showError('modules.query_error');
		}
	}

	/**
	 * Groups the provided entries by rounded coordinates
	 *
	 * @param entries - Entries to group
	 * @returns Entry groups
	 */
	public groupEntries(entries: Entry[]): Entry[][] {
		const groups = new Map<string, Entry[]>();

		entries.forEach((entry) => {
			const key = `${entry.lat.toFixed(5)}|${entry.long.toFixed(5)}`;
			if (!groups.has(key)) {
				groups.set(key, []);
			}
			groups.get(key)?.push(entry);
		});

		return Array.from(groups.values());
	}

	/**
	 * Loads all approved entries for the current course
	 * Loads all pages from the paginated API
	 *
	 * @author Julien Tietke <j.tietke@tj-labs.de>
	 */
	private async loadApprovedEntries(): Promise<void> {
		console.log('MapViewModel - courseId:', this.courseId);

		if (!this.courseId) {
			console.log('MapViewModel - No courseId found');
			this.isLoadingEntries = false;
			return;
		}

		const authToken = this.authStore.getAuthToken();
		console.log('MapViewModel - Loading entries for course:', this.courseId);

		try {
			// First, get the number of pages
			const numberOfPages = await this.entryService.getNumberOfPagesApproved(this.courseId, authToken);
			console.log('MapViewModel - Number of pages with approved entries:', numberOfPages);

			// Load all pages (API expects 1-based page index)
			const allEntries: Entry[] = [];
			for (let page = 1; page <= numberOfPages; page++) {
				console.log(`MapViewModel - Loading page ${page}/${numberOfPages}`);
				const pageEntries = await this.entryService.getApprovedEntries(this.courseId, authToken, page);
				allEntries.push(...pageEntries);
			}

			console.log('MapViewModel - Total approved entries loaded:', allEntries.length);
			console.log('MapViewModel - All approved entries:', allEntries);

			// Filter for entries with valid coordinates
			this.approvedEntries = allEntries.filter(
				(entry) =>
					entry.lat != null &&
					entry.long != null &&
					!isNaN(entry.lat) &&
					!isNaN(entry.long) &&
					entry.lat !== 0 &&
					entry.long !== 0,
			);
			console.log('MapViewModel - Approved entries with valid coordinates:', this.approvedEntries.length);
			console.log('MapViewModel - Valid entries data:', this.approvedEntries);

			this.isLoadingEntries = false;
		} catch (error) {
			console.error('MapViewModel - Error loading entries:', error);
			this.snackbarStore.showError('entries.query_error');
			this.isLoadingEntries = false;
		}
	}

	/**
	 * Gets the Mapbox access token from environment variables
	 *
	 * @returns The Mapbox public access token
	 */
	public getMapboxToken(): string {
		return process.env.REACT_APP_MAPBOX_PUBLIC_ACCESS_TOKEN || '';
	}

	/**
	 * Gets the Mapbox style URL with the access token
	 *
	 * @returns The complete Mapbox style URL
	 */
	public getMapStyle(): string {
		const token = this.getMapboxToken();
		return `https://api.mapbox.com/styles/v1/klims21/cm7nbq0wg00ys01r3h19tf2ct?access_token=${token}`;
	}

	/**
	 * Checks if the Mapbox token is available
	 *
	 * @returns True if token is configured, false otherwise
	 */
	public hasMapboxToken(): boolean {
		return !!this.getMapboxToken();
	}

	/**
	 * Updates the map viewport state
	 *
	 * @param newViewState - The new viewport state
	 */
	public updateViewState(newViewState: any): void {
		this.viewState = {
			longitude: newViewState.longitude,
			latitude: newViewState.latitude,
			zoom: newViewState.zoom,
		};
	}

	/**
	 * Resets the viewport to the default Germany view
	 *
	 * @author Julien Tietke <j.tietke@tj-labs.de>
	 */
	public resetViewToGermany(): void {
		this.viewState = {
			longitude: 10.4541194,
			latitude: 51.1787426,
			zoom: 5,
		};
	}

	/**
	 * Centers the map on specific coordinates
	 *
	 * @param latitude - Latitude to center on
	 * @param longitude - Longitude to center on
	 */
	public centerOnCoordinates(latitude: number, longitude: number): void {
		this.viewState = {
			...this.viewState,
			latitude,
			longitude,
			zoom: Math.max(this.viewState.zoom, 16),
		};
	}

	public showMapError(messageId: string): void {
		this.snackbarStore.showError(messageId);
	}

	/**
	 * Opens the entry detail modal by loading the entry and storing it in the EntriesReviewStore
	 *
	 * @param entryId - Identifier of the entry to open
	 * @author Julien Tietke <j.tietke@tj-labs.de>
	 */
	public openEntryDetail(entryId: string): void {
		const cachedEntry = this.approvedEntries.find((entry) => entry.id === entryId);
		if (cachedEntry) {
			this.entriesReviewStore.setEntry(this.parseEntrySelectedOptions(cachedEntry));
			return;
		}

		const authToken = this.authStore.getAuthToken();

		this.entryService
			.getEntry(entryId, authToken)
			.then((entry) => {
				this.entriesReviewStore.setEntry(this.parseEntrySelectedOptions(entry));
			})
			.catch(() => {
				this.snackbarStore.showError('map.fetch_entry_error');
			});
	}

	/**
	 * Ensures selectedOptions are available as structured data for the detail dialog.
	 * @param entry Entry payload returned by the API.
	 * @returns Entry with parsed selectedOptions map when possible.
	 * @author Julien Tietke <j.tietke@tj-labs.de>
	 */
	private parseEntrySelectedOptions(entry: Entry): Entry {
		if (!entry.selectedOptions || typeof entry.selectedOptions !== 'string') {
			return entry;
		}

		try {
			const parsed = JSON.parse(entry.selectedOptions) as SelectedOptionsMap;
			return {
				...entry,
				selectedOptions: parsed,
			};
		} catch {
			return entry;
		}
	}

	/**
	 * Returns the currently selected entry for the detail view
	 *
	 * @returns Entry or undefined
	 */
	public getEntry(): Entry | undefined {
		return this.entriesReviewStore.getEntry();
	}

	/**
	 * Indicates whether the entry detail dialog should be shown
	 *
	 * @returns True if an entry detail is open
	 */
	public isDetailView(): boolean {
		return !!this.entriesReviewStore.getEntry();
	}
}
